/**
 * XP computation (R7.1/R7.2/R7.6/R7.7, Rewards v1 + Phase 2b + Phase 2c
 * "Weekly Review" scope — `docs/specs/phase-2a-rewards.md` RW-1/RW-2/RW-3,
 * `docs/specs/phase-2b-habits.md` §5, `docs/specs/phase-2c-weekly-review.md`
 * §5/WR-15/WR-16/WR-17). A pure function of (event log, latest config) — no
 * stored counters, fully recomputable (AC-R-S3/AC-B-S3/AC-W-S3).
 *
 * Live XP sources as of this pass — ALL 8 `XpValues` fields, first time in
 * the product's history (AC-W22):
 *   - `action.completed`                                  -> xp.actionCompleted
 *   - `objective.target_date_adjusted` (source:
 *      'forecast_suggestion' ONLY, never 'manual', RW-3)   -> xp.dateAdjustmentAccepted
 *   - `checkin.completed` (first per occurredOn only,
 *      AC-B15's documented collapse rule)                  -> xp.checkinCompleted
 *   - `habit.completed`                                    -> xp.habitCompleted
 *   - `habit.clean_logged`                                 -> xp.avoidCleanDay
 *   - `review.completed` (once per distinct weekStart)      -> xp.reviewCompleted
 *   - `habit.target_adjusted` (source: 'review_suggestion'
 *      ONLY, never 'manual', mirrors RW-3)                  -> xp.adjustmentAccepted
 *   - a closed week's `WeekOutcome.met === true`, recognized
 *      at THAT week's `review.completed` (see below)         -> xp.weeklyTargetBonus
 *
 * `checkin.completed` collapse (AC-B15): commands.ts's `withImplicitCheckin`
 * may append MORE THAN ONE `checkin.completed` event for the same
 * `occurredOn` over the life of a day (harmless, since every logging command
 * appends one unconditionally when none exists yet at command-construction
 * time — see that module's doc comment) OR a caller could in principle
 * replay/import duplicates. Either way, only the FIRST (canonical eventId
 * order) active `checkin.completed` per distinct `occurredOn` earns XP here
 * — every subsequent one for the same day is worth 0, so XP is never
 * farmable by re-triggering check-in completion. `review.completed` uses the
 * identical collapse rule, keyed by `weekStart` instead of `occurredOn`
 * (AC-W1: completing the same week's review twice never double-pays).
 *
 * `weeklyTargetBonus` TIMING DECISION (phase-2c-weekly-review.md §5 flags
 * this as the one piece without a clean precedent — dev-core-domain's call,
 * documented here): **posture 2, recognized at review-completion time**, not
 * lazily folded into every `getRewardSnapshot()` read. Rationale:
 * 1. §5's one hard constraint is "must be visible and attributable" (R7.8) —
 *    tying the bonus to the SAME `review.completed` event that pays
 *    `reviewCompleted` gives it a concrete origin event and a concrete
 *    celebration beat (the finish-review moment), rather than a number that
 *    silently grows between two snapshot reads with no attributable cause.
 * 2. It stays a pure function of the log with NO stored counter (AC-W-S3):
 *    the bonus is not written as its own event — it is *computed* every time
 *    from (a) which weeks have a `review.completed` event (the "this week is
 *    closed and reviewed" signal) and (b) each habit's `WeekOutcome.met` for
 *    that week, recomputed fresh from `habit.completed`/`habit.lapse_logged`
 *    exactly as `../review/weekly-review.ts` does. Nothing new is persisted;
 *    `review.completed` already existing IS the trigger.
 * 3. "Once per habit per closed week" (AC-W20) falls out for free: a given
 *    `weekStart` has at most one surviving `review.completed` (the collapse
 *    rule above), so the bonus for (habitId, weekStart) can only ever be
 *    computed once no matter how many times completion is attempted.
 * 4. Using `review.completed`'s occurrence (not the calendar alone) as the
 *    gate also means a week that's closed but never reviewed pays no
 *    weeklyTargetBonus yet — consistent with WR-16's "this fires as part of
 *    the week-close computation the review screen surfaces, not as a new
 *    mid-week daily-page mechanic" and with D16 (no penalty for reviewing
 *    late: the bonus is simply waiting, not lost, exactly like
 *    `reviewCompleted` itself).
 * Habits considered are every habit that EXISTED (was created) by that
 * week's Sunday, evaluated with the schedule active that week (ADR-0006) —
 * paused/archived status is intentionally NOT checked here (unlike the
 * review's live `HabitWeekReport` table, WR-2): a habit paused or archived
 * AFTER a week closed must not retroactively lose a bonus it already earned
 * for a week it was active during, mirroring the "past weeks are never
 * re-judged" principle.
 *
 * Reverted events (ADR-0002: `log_entry.reverted`) are treated as absent —
 * XP recomputes down to (never below) the no-event baseline. This is the
 * anti-punitive guarantee (R7.11/AC-R4): reverting a completion removes the
 * XP it would have earned, but nothing ever goes negative, and nothing
 * "already banked" from OTHER events is touched. `habit.lapse_logged` is
 * deliberately ABSENT from this module (R7.7: "never subtracts anything") —
 * logging a lapse never earns, never costs XP.
 *
 * Phase 5 addition (ADR-0002 addendum 2026-07-04 D7): an objective's
 * `ambientRewardOnComplete` toggle (default true) suppresses ONLY its own
 * `action.completed` XP when off — no other XP source is touched (AC-O22).
 * The award is DROPPED, not zeroed, mirroring `computeCheckinXpAwards`'s own
 * "never appears as a spurious zero-amount award" convention.
 */

import type { CompassEvent, RewardConfig } from '../../contracts';
import { eventsOfTypes, excludingReverted, sortByEventId } from '../event-log/event-log';
import { countBuildCompletionsInWeek, countLapsesInWeek } from '../habits/logging';
import { buildHabitStates, weeklyPlanMet, weeklyPlannedFor } from '../habits/state';
import { buildObjectiveStates } from '../objectives/state';

/** One XP-earning contribution, kept for badge/explainer derivation reuse. */
export interface XpAward {
  readonly eventId: CompassEvent['eventId'];
  readonly amount: number;
  readonly source: keyof RewardConfig['xp'];
}

/**
 * Every XP-earning award in the log, honoring reverts, under the given
 * config. Order is canonical log order (callers needing "as of the Nth
 * award" — e.g. badge-earned-date derivation — can rely on this).
 */
export function computeXpAwards(
  events: readonly CompassEvent[],
  config: RewardConfig,
): readonly XpAward[] {
  const active = excludingReverted(events);
  const awards: XpAward[] = [];

  // Phase 5 (D7): built once, mirroring how computeWeeklyTargetBonusAwards
  // already builds buildHabitStates(active) once for its own loop, so every
  // action.completed event can cheaply look up its objective's
  // ambientRewardOnComplete toggle without a per-event full state rebuild.
  const objectiveStates = buildObjectiveStates(active);

  for (const event of eventsOfTypes(active, ['action.completed'])) {
    const objective = objectiveStates.get(event.objectiveId);
    // Suppressed, not zeroed: dropped from the award list entirely so it
    // never appears as a spurious zero-amount entry (mirrors
    // computeCheckinXpAwards's identical convention for collapsed
    // duplicates). Scope is deliberately narrow (AC-O22): this toggle gates
    // ONLY this objective's own action.completed XP -- no other source in
    // this function reads it.
    if (objective && !objective.ambientRewardOnComplete) continue;
    awards.push({
      eventId: event.eventId,
      amount: config.xp.actionCompleted,
      source: 'actionCompleted',
    });
  }

  for (const event of eventsOfTypes(active, [
    'objective.target_date_adjusted',
  ])) {
    // RW-3: only an ACCEPTED FORECAST SUGGESTION earns the bonus. A manual
    // date edit (source: 'manual') is bookkeeping, not honesty-under-
    // pressure — paying it identically would let XP be farmed by editing
    // dates freely with no pace pressure behind them (phase-2a-rewards.md
    // §1 RW-3, AC-R3).
    if (event.source !== 'forecast_suggestion') continue;
    awards.push({
      eventId: event.eventId,
      amount: config.xp.dateAdjustmentAccepted,
      source: 'dateAdjustmentAccepted',
    });
  }

  for (const event of eventsOfTypes(active, ['habit.completed'])) {
    awards.push({
      eventId: event.eventId,
      amount: config.xp.habitCompleted,
      source: 'habitCompleted',
    });
  }

  for (const event of eventsOfTypes(active, ['habit.clean_logged'])) {
    awards.push({
      eventId: event.eventId,
      amount: config.xp.avoidCleanDay,
      source: 'avoidCleanDay',
    });
  }

  for (const event of eventsOfTypes(active, ['habit.target_adjusted'])) {
    // Mirrors RW-3 exactly for the habit side (WR-9/WR-11/AC-W11/AC-W13):
    // only an accepted REVIEW SUGGESTION earns the realism bonus. A manual
    // schedule edit (source: 'manual', HB-4's unchanged path) is bookkeeping
    // and never reaches this branch.
    if (event.source !== 'review_suggestion') continue;
    awards.push({
      eventId: event.eventId,
      amount: config.xp.adjustmentAccepted,
      source: 'adjustmentAccepted',
    });
  }

  awards.push(...computeCheckinXpAwards(active, config));
  awards.push(...computeReviewXpAwards(active, config));
  awards.push(...computeWeeklyTargetBonusAwards(active, config));
  awards.push(...computeDayClosedXpAwards(active, config));
  awards.push(...computeStandalonePriorityXpAwards(active, config));

  // Canonical log order (eventId lexicographic) so "the Nth award" and
  // level-up detection walk XP chronologically, not by which event type was
  // scanned first above.
  return [...awards].sort((a, b) =>
    a.eventId < b.eventId ? -1 : a.eventId > b.eventId ? 1 : 0,
  );
}

/**
 * `checkin.completed` awards, one per DISTINCT `occurredOn` -- the first
 * (canonical eventId order) active event for that day earns
 * `xp.checkinCompleted`; any further ones for the same day are dropped
 * entirely (not merely zero-valued) so they never appear as a spurious
 * zero-amount award either (module doc comment: "never farmable by
 * re-triggering check-in completion").
 */
function computeCheckinXpAwards(
  active: readonly CompassEvent[],
  config: RewardConfig,
): readonly XpAward[] {
  const seen = new Set<string>();
  const awards: XpAward[] = [];
  // Sorted defensively (canonical eventId order) so "the first per day" is
  // well-defined even if a caller's `events` array is not yet canonically
  // ordered (mirrors config.ts's resolveRewardConfig's own defensive sort).
  for (const event of eventsOfTypes(sortByEventId(active), ['checkin.completed'])) {
    if (seen.has(event.occurredOn)) continue;
    seen.add(event.occurredOn);
    awards.push({
      eventId: event.eventId,
      amount: config.xp.checkinCompleted,
      source: 'checkinCompleted',
    });
  }
  return awards;
}

/**
 * `review.completed` awards, one per DISTINCT `weekStart` (WR-15/AC-W19,
 * mirrors `computeCheckinXpAwards`'s exact collapse pattern keyed by week
 * instead of day). Full credit regardless of how late the review was
 * completed (WR-5/AC-W1) — this function has no notion of "late" at all, it
 * only asks "does an active review.completed exist for this week".
 */
function computeReviewXpAwards(
  active: readonly CompassEvent[],
  config: RewardConfig,
): readonly XpAward[] {
  const seen = new Set<string>();
  const awards: XpAward[] = [];
  for (const event of eventsOfTypes(sortByEventId(active), ['review.completed'])) {
    if (seen.has(event.weekStart)) continue;
    seen.add(event.weekStart);
    awards.push({
      eventId: event.eventId,
      amount: config.xp.reviewCompleted,
      source: 'reviewCompleted',
    });
  }
  return awards;
}

/**
 * `dayClosed` awards (R8.9, Phase 6, AC-T21) -- one per DISTINCT
 * `occurredOn`, mirroring `computeCheckinXpAwards`'s exact collapse pattern:
 * the first (canonical eventId order) active `day.closed` event for a given
 * date earns `xp.dayClosed`; any further one for the same date is dropped
 * entirely (never a spurious zero-amount award). No "late" branch exists
 * here (AC-T23: fully-paid-late) -- this function only asks "does an active
 * day.closed exist for this date."
 */
function computeDayClosedXpAwards(
  active: readonly CompassEvent[],
  config: RewardConfig,
): readonly XpAward[] {
  const seen = new Set<string>();
  const awards: XpAward[] = [];
  for (const event of eventsOfTypes(sortByEventId(active), ['day.closed'])) {
    if (seen.has(event.occurredOn)) continue;
    seen.add(event.occurredOn);
    awards.push({
      eventId: event.eventId,
      amount: config.xp.dayClosed,
      source: 'dayClosed',
    });
  }
  return awards;
}

/**
 * `priorityCompleted` awards (R8.2, Phase 6) -- STANDALONE Priorities only:
 * a `priority.completed` event is only ever produced for a standalone
 * Priority (the ADR addendum's reconciliation rule: a linked Priority's
 * completion is `action.completed`, already paid above via
 * `actionCompleted`, never `priority.completed`), so this function needs no
 * linked/standalone branch of its own -- every surviving `priority.completed`
 * event in the log is, by construction, a standalone completion.
 */
function computeStandalonePriorityXpAwards(
  active: readonly CompassEvent[],
  config: RewardConfig,
): readonly XpAward[] {
  const awards: XpAward[] = [];
  for (const event of eventsOfTypes(active, ['priority.completed'])) {
    awards.push({
      eventId: event.eventId,
      amount: config.xp.priorityCompleted,
      source: 'priorityCompleted',
    });
  }
  return awards;
}

/**
 * `weeklyTargetBonus` awards (WR-16/AC-W20/AC-W21) — see the module doc
 * comment's "TIMING DECISION" for the full reasoning. For every week with a
 * surviving `review.completed` event, every habit that existed by that
 * week's Sunday earns the bonus once if its `WeekOutcome.met` was true that
 * week (build: actual >= planned; avoid: actual <= planned), symmetric
 * per D8. The `eventId` attributed to this award is the week's
 * `review.completed` event — there is no separate stored event for the
 * bonus itself (AC-W-S3: no new stored counter), so attributing it to the
 * event that unlocked it is the natural choice and keeps every award
 * traceable to a real log entry.
 */
function computeWeeklyTargetBonusAwards(
  active: readonly CompassEvent[],
  config: RewardConfig,
): readonly XpAward[] {
  const reviewEvents = eventsOfTypes(sortByEventId(active), ['review.completed']);
  const seenWeeks = new Set<string>();
  const states = buildHabitStates(active);
  const awards: XpAward[] = [];

  for (const review of reviewEvents) {
    if (seenWeeks.has(review.weekStart)) continue; // AC-W1: a later duplicate review.completed for the same week never re-pays
    seenWeeks.add(review.weekStart);

    for (const state of states.values()) {
      if (state.createdOn > review.weekStart) continue; // habit didn't exist yet this week
      const planned = weeklyPlannedFor(state.plan);
      const actual =
        state.plan.habitKind === 'build'
          ? countBuildCompletionsInWeek(active, state.habitId, review.weekStart)
          : countLapsesInWeek(active, state.habitId, review.weekStart);
      const met = weeklyPlanMet(state.plan.habitKind, actual, planned);
      if (!met) continue;

      awards.push({
        eventId: review.eventId,
        amount: config.xp.weeklyTargetBonus,
        source: 'weeklyTargetBonus',
      });
    }
  }

  return awards;
}

/** Lifetime XP total — the sum of every award, never negative by construction. */
export function computeXpTotal(
  events: readonly CompassEvent[],
  config: RewardConfig,
): number {
  return computeXpAwards(events, config).reduce(
    (sum, award) => sum + award.amount,
    0,
  );
}
